import {
  Context,
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from 'react';
import DiscardChangesModal from './DiscardChangesModal';

export type DiscardChangesModalContextValue = {
  onCloseModal: (hasChanges: boolean) => void;
};

export const DiscardChangesModalContext: Context<
  DiscardChangesModalContextValue | undefined
> = createContext<DiscardChangesModalContextValue | undefined>(undefined);

export const DiscardChangesModalContextProvider = ({
  children,
  onCloseModal,
}: PropsWithChildren & {
  onCloseModal: () => void;
}) => {
  const [showDiscardChangesModal, setShowDiscardChangesModal] =
    useState<boolean>(false);

  const handleCloseModal = useCallback(
    (hasChanges: boolean) => {
      if (hasChanges) {
        setShowDiscardChangesModal(true);
      } else {
        onCloseModal();
      }
    },
    [onCloseModal],
  );

  return (
    <DiscardChangesModalContext.Provider
      value={{ onCloseModal: handleCloseModal }}
    >
      {children}
      <DiscardChangesModal
        isOpen={showDiscardChangesModal}
        onClose={() => setShowDiscardChangesModal(false)}
        onConfirm={() => {
          setShowDiscardChangesModal(false);
          onCloseModal();
        }}
      />
    </DiscardChangesModalContext.Provider>
  );
};

export const useDiscardChangesModalContext =
  (): DiscardChangesModalContextValue => {
    const context = useContext(DiscardChangesModalContext);
    if (!context) {
      throw new Error(
        'useDiscardChangesModalContext must be used within a DiscardChangesModalContextProvider.',
      );
    }
    return context;
  };
